import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import { formatMoney, PAYMENT_METHODS } from "@/lib/api";
import {
  renderBrandedHeader,
  renderBrandedFooter,
  brandedTableHeadStyles,
  drawSignatureBlock,
} from "@/lib/pdfBranding";

const COLORS = { primary: [27, 67, 50], muted: [110, 115, 110], text: [26, 28, 26] };

const COURTESY_REASONS = [
  { key: "cortesia_familiar", label: "Cortesía familiar" },
  { key: "garantia",          label: "Garantía" },
  { key: "reposicion",        label: "Reposición" },
  { key: "otro",              label: "Otro" },
];

/**
 * Genera un PDF de recibo (RC-XXXXX) reutilizable tanto en la finalización
 * de cita como en la re-descarga desde la ficha del paciente.
 *
 * Params:
 *   patient        — objeto paciente (name, document_number, ...)
 *   appointment    — objeto cita (start, ...)
 *   evolucion      — { diente, diagnostico, procedimiento }
 *   items          — [{ tooth, treatment_name, price_plan, price_charged, change_reason }]
 *   settlement     — "cobrado" | "cxc" | "cortesia"
 *   payments       — [{ method, amount }]
 *   applied        — { anticipo, credit }
 *   total          — total_charged
 *   ar             — { id? } cxc reference (opcional)
 *   courtesy       — { reason, notes } (opcional)
 *   doctor         — objeto usuario odontólogo (con signature_image)
 *   doctorName     — fallback si no hay doctor
 *   branding       — branding del tenant
 *   receiptNumber  — string "RC-00001"
 */
export function generateReceiptPDF({
  patient,
  appointment,
  evolucion,
  items,
  settlement,
  payments,
  applied,
  total,
  ar,
  courtesy,
  doctor,
  doctorName,
  branding,
  receiptNumber,
}) {
  const doc = new jsPDF({ unit: "pt", format: "letter" });
  const W = doc.internal.pageSize.getWidth();
  const margin = 48;
  let y = renderBrandedHeader(doc, branding, { title: receiptNumber || "RECIBO", subtitle: "Recibo de atención" });

  doc.setTextColor(...COLORS.text); doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Paciente", margin, y); y += 14;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  doc.text(patient?.name || "—", margin, y); y += 12;
  if (patient?.document_number) { doc.text(`Doc: ${patient.document_type || ""} ${patient.document_number}`, margin, y); y += 12; }
  if (patient?.phone) { doc.text(`Tel: ${patient.phone}`, margin, y); y += 12; }
  if (appointment?.start) {
    doc.text(`Fecha de atención: ${new Date(appointment.start).toLocaleString("es-CO", { dateStyle: "medium", timeStyle: "short" })}`, margin, y);
    y += 12;
  }
  if (doctor || doctorName) {
    const dn = (doctor && doctor.name) || doctorName;
    doc.text(`Profesional: ${dn || ""}`, margin, y); y += 12;
    if (doctor?.specialty) { doc.text(`Especialidad: ${doctor.specialty}`, margin, y); y += 12; }
    if (doctor?.registry_number) { doc.text(`Registro: ${doctor.registry_number}`, margin, y); y += 12; }
  }
  y += 8;

  // Procedimiento
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  doc.text("Procedimiento realizado", margin, y); y += 14;
  doc.setFont("helvetica", "normal"); doc.setFontSize(10);
  if (evolucion?.diente) { doc.text(`Diente: ${evolucion.diente}`, margin, y); y += 12; }
  if (evolucion?.diagnostico) { doc.text(`Diagnóstico: ${evolucion.diagnostico}`, margin, y); y += 12; }
  const procWrap = doc.splitTextToSize(evolucion?.procedimiento || "—", W - 2 * margin);
  doc.text(procWrap, margin, y); y += procWrap.length * 12 + 6;

  // Items
  if (items && items.length > 0) {
    doc.setFont("helvetica", "bold"); doc.setFontSize(11);
    doc.text("Procedimientos cobrados", margin, y); y += 6;
    autoTable(doc, {
      startY: y + 4,
      head: [["Diente", "Tratamiento", "Valor"]],
      body: items.map((it) => [
        it.tooth || "—",
        it.treatment_name || "—",
        formatMoney(it.price_charged || 0),
      ]),
      theme: "grid",
      headStyles: brandedTableHeadStyles(branding),
      bodyStyles: { fontSize: 9, textColor: COLORS.text },
      columnStyles: { 2: { halign: "right", fontStyle: "bold" } },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 12;
  }

  // Liquidación
  doc.setFont("helvetica", "bold"); doc.setFontSize(11);
  if (settlement === "cobrado") {
    doc.text("Cobro registrado", margin, y); y += 6;
    const rows = [];
    if (applied?.anticipo > 0) rows.push(["Anticipo aplicado", formatMoney(applied.anticipo)]);
    if (applied?.credit > 0)   rows.push(["Saldo a favor aplicado", formatMoney(applied.credit)]);
    (payments || []).filter((p) => Number(p.amount) > 0).forEach((p) => {
      const lbl = (PAYMENT_METHODS.find((m) => m.key === p.method) || {}).label || p.method;
      rows.push([lbl, formatMoney(p.amount)]);
    });
    rows.push(["TOTAL", formatMoney(total)]);
    autoTable(doc, {
      startY: y + 4,
      body: rows,
      theme: "plain",
      bodyStyles: { fontSize: 10, textColor: COLORS.text },
      columnStyles: { 0: { fontStyle: "bold", cellWidth: 220 }, 1: { halign: "right", fontStyle: "bold" } },
      margin: { left: margin, right: margin },
    });
    y = doc.lastAutoTable.finalY + 16;
  } else if (settlement === "cxc") {
    doc.text("Cuenta por cobrar", margin, y); y += 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    doc.text(`Esta cita queda como CxC del paciente por ${formatMoney(total)}.`, margin, y); y += 14;
    if (ar?.id) { doc.text(`Referencia CxC: ${ar.id.slice(0, 8)}`, margin, y); y += 12; }
  } else if (settlement === "cortesia") {
    doc.text("Cortesía / no se cobra", margin, y); y += 14;
    doc.setFont("helvetica", "normal"); doc.setFontSize(10);
    const reasonLabel = (COURTESY_REASONS.find((r) => r.key === courtesy?.reason) || {}).label || courtesy?.reason || "—";
    doc.text(`Motivo: ${reasonLabel}`, margin, y); y += 12;
    if (courtesy?.notes) { doc.text(`Notas: ${courtesy.notes}`, margin, y); y += 12; }
    doc.text(`Valor no cobrado: ${formatMoney(total)}`, margin, y); y += 12;
  }

  // Firmas
  if (y > 600) { doc.addPage(); y = margin + 60; } else y += 60;
  const sigW = (W - 2 * margin - 30) / 2;
  drawSignatureBlock(doc, {
    x: margin,
    baselineY: y,
    width: sigW,
    signatureImage: null,
    label: "Firma del paciente",
    name: patient?.name || "",
    subtitle: patient?.document_number ? `${patient.document_type || "CC"} ${patient.document_number}` : "",
  });
  drawSignatureBlock(doc, {
    x: margin + sigW + 30,
    baselineY: y,
    width: sigW,
    signatureImage: doctor?.signature_image || null,
    label: "Firma del profesional",
    name: (doctor && doctor.name) || doctorName || "",
    subtitle: doctor?.signature_text || doctor?.registry_number || "",
  });
  renderBrandedFooter(doc, branding);

  const safeName = (receiptNumber || patient?.name || "paciente").replace(/\s+/g, "_");
  doc.save(`recibo_${safeName}_${new Date().toISOString().slice(0, 10)}.pdf`);
}
